import React from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ChevronDown, Sparkles, Users, HelpCircle, ArrowUpRight } from "lucide-react";
import { useLang } from "@/i18n";
import useContent from "@/hooks/useContent";
import { Button } from "@/components/ui/button";
import { DarkPage, Reveal, SectionKicker, WakeMarquee, SplitSpectatorActor, HowBetoolWorks, ReadyToPublish, TestimonialFeed, FaqList, FinalCta, IMG } from "@/components/marketing";

const WAKE = ["wake_2", "wake_3", "wake_5", "wake_1", "wake_6", "wake_8"];

export default function Landing() {
  const { t } = useLang();
  const nav = useNavigate();
  const content = useContent();

  return (
    <DarkPage>
      {/* HERO */}
      <section className="relative max-w-7xl mx-auto px-6 lg:px-8 pt-16 md:pt-24 pb-20">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <Reveal>
              <div className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-4 py-1.5 text-sm font-semibold text-violet-200 mb-6 uppercase tracking-[0.15em]" data-testid="hero-badge">
                <Sparkles size={14} /> {t("hero_left_eyebrow")}
              </div>
              <h1 className="font-head font-black text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight mb-6 whitespace-pre-line" data-testid="hero-title">
                {t("hero_left_title")}
              </h1>
              <p className="text-zinc-300 text-base lg:text-lg leading-relaxed max-w-xl mb-8" data-testid="hero-sub">{t("hero_left_sub")}</p>
              <div className="flex flex-wrap gap-3">
                <Button onClick={() => nav("/auth?mode=signup")} data-testid="hero-cta" className="rounded-full bt-gradient text-white hover:opacity-90 text-base font-bold px-7 min-h-[58px]">
                  {t("hero2_cta")} <ArrowRight size={18} className="ml-2" />
                </Button>
                <a href="#explainer">
                  <Button variant="outline" data-testid="hero-cta2" className="rounded-full border-white/25 bg-transparent text-white hover:bg-white/10 text-base font-bold px-7 min-h-[58px]">
                    <ChevronDown size={16} className="mr-2" /> {t("hero2_cta2")}
                  </Button>
                </a>
              </div>
            </Reveal>
          </div>
          <Reveal delay={0.15} className="relative">
            <div className="absolute -inset-6 bg-violet-600/20 blur-3xl rounded-full" />
            <img src={IMG.hero} alt="BETOOL" className="relative rounded-[2rem] border border-white/10 w-full object-cover shadow-2xl" data-testid="hero-image" />
          </Reveal>
        </div>
      </section>

      {/* HOW BETOOL WORKS — 5-STEP VISUAL EXPLAINER */}
      <section id="explainer" className="max-w-7xl mx-auto px-6 lg:px-8 py-20 scroll-mt-24">
        <HowBetoolWorks />
      </section>

      {/* SPECTATOR VS ACTOR */}
      <section className="max-w-7xl mx-auto px-6 lg:px-8 py-20">
        <Reveal className="max-w-3xl mb-10">
          <SectionKicker>{t("wake_title")}</SectionKicker>
          <h2 className="font-head font-black text-3xl sm:text-4xl lg:text-5xl leading-tight tracking-tight mb-5">{t("split_title")}</h2>
          <p className="text-zinc-400 text-lg leading-relaxed">{t("split_intro")}</p>
        </Reveal>
        <SplitSpectatorActor />
      </section>

      {/* WAKE-UP MARQUEE */}
      <WakeMarquee lines={WAKE.map((k) => t(k))} />

      {/* JOIN BAND — creator visual + marketing message + CTA */}
      <section className="max-w-7xl mx-auto px-6 lg:px-8 py-20">
        <div className="relative overflow-hidden rounded-[2rem] border border-white/10 bg-gradient-to-br from-violet-600/15 via-[#0e0b1e] to-[#050505]">
          <div className="pointer-events-none absolute -top-24 -right-16 w-[28rem] h-[28rem] bg-violet-600/20 blur-3xl rounded-full" />
          <div className="relative grid lg:grid-cols-2 gap-10 items-center p-6 sm:p-10 lg:p-14">
            <Reveal className="relative">
              <img src={IMG.community[2]} alt="BETOOL" className="rounded-3xl border border-white/10 w-full aspect-[4/3] object-cover object-center shadow-2xl" data-testid="join-band-image" />
            </Reveal>
            <Reveal delay={0.1}>
              <h2 className="font-head font-black text-3xl sm:text-4xl lg:text-5xl leading-tight tracking-tight mb-4" data-testid="join-band-title">{t("join_band_title")}</h2>
              <p className="text-zinc-300 text-lg leading-relaxed mb-8" data-testid="join-band-sub">{t("join_band_sub")}</p>
              <Button onClick={() => nav("/auth?mode=signup")} data-testid="join-band-cta" className="rounded-full bt-gradient text-white hover:opacity-90 text-lg font-bold px-9 min-h-[60px]">{t("hero2_cta")} <ArrowRight size={18} className="ml-2" /></Button>
            </Reveal>
          </div>
        </div>
      </section>

      {/* COMMUNITY */}
      <section className="max-w-7xl mx-auto px-6 lg:px-8 py-24">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <Reveal className="relative grid grid-cols-2 gap-4">
            <img src={IMG.community[0]} alt="community" className="rounded-3xl border border-white/10 w-full aspect-video object-cover object-center" />
            <img src={IMG.community[1]} alt="community" className="rounded-3xl border border-white/10 w-full aspect-video object-cover object-center" />
          </Reveal>
          <Reveal delay={0.1}>
            <SectionKicker>{t("nav_community")}</SectionKicker>
            <h2 className="font-head font-black text-3xl sm:text-4xl lg:text-5xl leading-tight tracking-tight mb-4">{t("comm_title")}</h2>
            <p className="text-zinc-300 text-lg mb-6">{t("comm_sub")}</p>
            <ul className="space-y-3 mb-8">
              {["comm_1", "comm_3", "comm_4", "comm_6"].map((k) => (
                <li key={k} className="flex gap-3 text-zinc-300"><span className="mt-2 w-1.5 h-1.5 rounded-full bt-gradient shrink-0" />{t(k)}</li>
              ))}
            </ul>
            <Link to="/communaute"><Button variant="outline" data-testid="community-link" className="rounded-full border-white/25 bg-transparent text-white hover:bg-white/10 font-bold"><Users size={16} className="mr-2" />{t("comm_join")}</Button></Link>
          </Reveal>
        </div>
      </section>

      {/* READY TO PUBLISH */}
      <section className="max-w-7xl mx-auto px-6 lg:px-8 py-20">
        <Reveal className="max-w-2xl mb-12">
          <h2 className="font-head font-black text-3xl sm:text-4xl lg:text-5xl tracking-tight mb-3">{t("ready2_title")}</h2>
          <p className="text-zinc-400 text-lg">{t("ready2_sub")}</p>
        </Reveal>
        <ReadyToPublish />
      </section>

      {/* COMMUNITY FEED */}
      <section className="max-w-7xl mx-auto px-6 lg:px-8 py-20">
        <Reveal className="mb-8"><h2 className="font-head font-black text-3xl sm:text-4xl lg:text-5xl tracking-tight">{t("feed2_title")}</h2></Reveal>
        <TestimonialFeed testimonials={(content?.testimonials || [])} />
      </section>

      {/* FAQ PREVIEW */}
      <section className="max-w-3xl mx-auto px-6 lg:px-8 py-20">
        <Reveal className="mb-6 text-center">
          <SectionKicker><HelpCircle size={13} className="inline" /> FAQ</SectionKicker>
          <h2 className="font-head font-black text-3xl sm:text-4xl lg:text-5xl tracking-tight mb-2">{t("faq2_title")}</h2>
          <p className="text-zinc-400 text-lg">{t("faq2_sub")}</p>
        </Reveal>
        <FaqList items={(content?.faq || []).slice(0, 6)} testid="faq-preview" />
        <div className="text-center mt-8"><Link to="/faq"><Button variant="outline" data-testid="faq-link" className="rounded-full border-white/25 bg-transparent text-white hover:bg-white/10 font-bold">{t("faq_all")} <ArrowUpRight size={16} className="ml-1" /></Button></Link></div>
      </section>

      {/* FINAL CTA */}
      <FinalCta content={content} />
    </DarkPage>
  );
}
